import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchCompanyDriverDetail, fetchMyCompany } from '../services/api/companyOwnerAuth';
import { getCompanyOwnerAuthUser, saveCompanyOwnerCompany } from '../services/storage/companyOwnerFlow';
import { CompanyDriverDetail } from '../types/companyOwner';
import {
  formatCurrency,
  formatDateTime,
  getNonCompanyAccessFallbackRoute,
  isCompanyOwnerUser,
} from '../utils/companyOwner';
import '../styles/company-owner.css';
import '../styles/register.css';

const CompanyOwnerDriverDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { driverId } = useParams<{ driverId: string }>();
  const companyOwnerUser = useMemo(() => getCompanyOwnerAuthUser(), []);
  const [driver, setDriver] = useState<CompanyDriverDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isCompanyOwnerUser(companyOwnerUser)) {
      navigate(getNonCompanyAccessFallbackRoute(), { replace: true });
      return;
    }

    if (!driverId) {
      navigate('/company/dashboard', { replace: true });
      return;
    }

    let mounted = true;

    const loadDriver = async () => {
      setLoading(true);
      setError('');

      try {
        const company = await fetchMyCompany();
        if (!mounted) return;

        if (!company) {
          navigate('/company/onboarding', { replace: true });
          return;
        }

        saveCompanyOwnerCompany(company);

        const detail = await fetchCompanyDriverDetail(driverId);
        if (!mounted) return;
        setDriver(detail);
      } catch (fetchError: any) {
        if (!mounted) return;
        setError(fetchError.message || 'Could not load company driver details.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    void loadDriver();

    return () => {
      mounted = false;
    };
  }, [companyOwnerUser, driverId, navigate]);

  if (loading) {
    return (
      <section className="register-page">
        <div className="register-shell register-shell-single">
          <div className="register-panel register-panel-form register-status-panel">
            <div className="register-loader" />
            <h1 className="register-form-title">Loading driver detail</h1>
            <p className="register-form-copy">Pulling the selected company driver profile.</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !driver) {
    return (
      <section className="register-page">
        <div className="register-shell register-shell-single">
          <div className="register-panel register-panel-form register-status-panel">
            <div className="register-status-badge register-status-badge-error">Driver Detail</div>
            <h1 className="register-form-title">We could not load this driver</h1>
            <p className="register-form-copy">{error || 'The driver detail is unavailable right now.'}</p>
            <div className="register-status-actions">
              <Link className="register-submit register-submit-inline" to="/company/dashboard">
                Back to dashboard
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="company-page">
      <div className="company-detail-shell">
        <div className="company-detail-header">
          <div>
            <div className="register-form-badge">Company Driver Detail</div>
            <h1 className="company-dashboard-title">{driver.name}</h1>
            <p className="company-dashboard-copy">
              Review this driver’s current operational state, earnings snapshot, and raw company
              onboarding details.
            </p>
          </div>
          <Link className="register-secondary-button" to="/company/dashboard">
            Back to dashboard
          </Link>
        </div>

        <div className="company-stats-grid">
          <div className="company-stat-card">
            <span className="company-stat-label">Current Status</span>
            <strong>{driver.onTrip ? 'On trip' : driver.online ? 'Online' : 'Offline'}</strong>
            <span className="company-stat-note">{driver.linkStatus}</span>
          </div>
          <div className="company-stat-card">
            <span className="company-stat-label">Today</span>
            <strong>{formatCurrency(driver.dailyEarnings)}</strong>
            <span className="company-stat-note">{driver.tripsToday} trips scheduled today</span>
          </div>
          <div className="company-stat-card">
            <span className="company-stat-label">This Week</span>
            <strong>{formatCurrency(driver.weeklyEarnings)}</strong>
            <span className="company-stat-note">{driver.completedTrips} total completed trips</span>
          </div>
          <div className="company-stat-card">
            <span className="company-stat-label">Rating</span>
            <strong>{driver.ratings.toFixed(1)}</strong>
            <span className="company-stat-note">{driver.ratingCount} ratings recorded</span>
          </div>
        </div>

        <div className="company-main-grid">
          <div className="company-main-column">
            <section className="company-panel">
              <div className="company-panel-header">
                <div>
                  <h2 className="company-panel-title">Operational visibility</h2>
                  <p className="company-panel-copy">What the dashboard knows right now about this driver.</p>
                </div>
              </div>

              <div className="company-detail-grid">
                <div className="company-detail-card">
                  <span>Email</span>
                  <strong>{driver.email}</strong>
                </div>
                <div className="company-detail-card">
                  <span>Phone</span>
                  <strong>{driver.phoneE164 || 'Not supplied'}</strong>
                </div>
                <div className="company-detail-card">
                  <span>Last seen</span>
                  <strong>{formatDateTime(driver.lastSeenAt)}</strong>
                </div>
                <div className="company-detail-card">
                  <span>Last known coordinates</span>
                  <strong>
                    {driver.lastKnownLat != null && driver.lastKnownLng != null
                      ? `${driver.lastKnownLat}, ${driver.lastKnownLng}`
                      : 'No live coordinate available'}
                  </strong>
                </div>
                <div className="company-detail-card">
                  <span>Acceptance Rate 30d</span>
                  <strong>{driver.acceptanceRate30d.toFixed(1)}%</strong>
                </div>
                <div className="company-detail-card">
                  <span>Average response 30d</span>
                  <strong>{driver.avgResponseSec30d} sec</strong>
                </div>
              </div>
            </section>
          </div>

          <div className="company-side-column">
            <section className="company-panel">
              <div className="company-panel-header">
                <div>
                  <h2 className="company-panel-title">Raw company data from onboarding</h2>
                  <p className="company-panel-copy">
                    This is the driver-supplied company information the backend matched against.
                  </p>
                </div>
              </div>

              <div className="company-review-card">
                <strong>{driver.rawCompanyName || 'No company name supplied'}</strong>
                <span>{driver.rawCompanyEmail || 'No company email supplied'}</span>
                <span>{driver.rawCompanyPhone || 'No company phone supplied'}</span>
                <span>{driver.rawCompanyRegNumber || 'No registration number supplied'}</span>
                <p>{driver.matchingNotes || 'No matching note has been recorded for this driver yet.'}</p>
              </div>
            </section>

            <section className="company-panel">
              <div className="company-panel-header">
                <div>
                  <h2 className="company-panel-title">Earnings summary</h2>
                  <p className="company-panel-copy">Visible earnings figures from the linked driver record.</p>
                </div>
              </div>

              <div className="company-detail-grid">
                <div className="company-detail-card">
                  <span>Today</span>
                  <strong>{formatCurrency(driver.dailyEarnings)}</strong>
                </div>
                <div className="company-detail-card">
                  <span>This week</span>
                  <strong>{formatCurrency(driver.weeklyEarnings)}</strong>
                </div>
                <div className="company-detail-card">
                  <span>This month</span>
                  <strong>{formatCurrency(driver.monthlyEarnings)}</strong>
                </div>
                <div className="company-detail-card">
                  <span>Total visible</span>
                  <strong>{formatCurrency(driver.totalEarnings)}</strong>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CompanyOwnerDriverDetailPage;
